import { Package, DollarSign, Truck, Calculator, AlertTriangle, ArrowRightLeft, Copy, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Summary Cards for Shipment Costing Page
 * Compact horizontal inline display for metrics with readable fonts
 */
const SummaryCards = ({ summary }) => {
  if (!summary) return null;

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "₹0";
    return `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const metrics = [
    {
      label: "Orders",
      value: summary.total_orders || summary.total_products,
      icon: Package,
      color: "text-blue-600",
      bg: "bg-blue-50",
      border: "border-blue-200",
      tooltip: "Total unique shipments (tracking numbers) across ALL clients including DP Clients, Partners, and Agents. Sales panel shows only DP CLIENT records."
    },
    {
      label: "Medicine",
      value: formatCurrency(summary.total_medicine_cost),
      icon: DollarSign,
      color: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200"
    },
    {
      label: "Shipping",
      value: formatCurrency(summary.total_shipping_cost),
      icon: Truck,
      color: "text-orange-600",
      bg: "bg-orange-50",
      border: "border-orange-200"
    },
    {
      label: "Total",
      value: formatCurrency(summary.total_cost),
      icon: Calculator,
      color: "text-purple-600",
      bg: "bg-purple-50",
      border: "border-purple-200",
      highlight: true
    },
    {
      label: "Errors",
      value: summary.orders_with_errors,
      icon: AlertTriangle,
      color: "text-red-600",
      bg: "bg-red-50",
      border: "border-red-200",
      hide: !summary.orders_with_errors
    },
    {
      label: "Shifted",
      value: summary.shifted_orders || 0,
      icon: ArrowRightLeft,
      color: "text-amber-600",
      bg: "bg-amber-50",
      border: "border-amber-200",
      hide: !(summary.shifted_orders)
    },
    {
      label: "Duplicates",
      value: summary.duplicate_orders || 0,
      icon: Copy,
      color: "text-orange-600",
      bg: "bg-orange-50",
      border: "border-orange-200",
      hide: !(summary.duplicate_orders)
    }
  ];

  return (
    <div className="flex flex-wrap items-center gap-3 py-2.5 px-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="summary-bar">
      {metrics.filter(m => !m.hide).map((metric, idx) => {
        const Icon = metric.icon;
        return (
          <div 
            key={idx} 
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md border ${metric.bg} ${metric.border} ${metric.highlight ? 'ring-2 ring-purple-300' : ''}`}
          >
            <Icon className={`w-4 h-4 ${metric.color}`} />
            <span className="text-sm text-slate-600">{metric.label}:</span>
            <span className={`text-base font-bold ${metric.color}`}>{metric.value}</span>
            {metric.tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-slate-400 cursor-help ml-0.5" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-xs">{metric.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SummaryCards;
